import clsx from 'clsx';
import { useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDescription,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useGetMattersQuery } from '@/services/api/mattersService';
import type { BaseMatter } from '@/services/types/client-intake-types';

interface MatterSelectorComboboxProps {
  /** Currently selected matter ID; empty string means "All matters" */
  value: string;
  /** Callback fired when the user selects a different matter */
  onChange: (id: string) => void;
  /** Optional additional Tailwind classes */
  className?: string;
  /** Placeholder text shown in the input */
  placeholder?: string;
  /** Accessible label for the combobox */
  'aria-label'?: string;
  /** Whether to show the special "All matters" option */
  includeAllOption?: boolean;
  /** Exclude options entirely from the list (hidden, not shown) */
  excludeFilter?: (matter: BaseMatter) => boolean;
  /** Disable option predicate (shown but grayed out, not selectable) */
  isOptionDisabled?: (matter: BaseMatter) => boolean;
  /** Optional firm ID to filter matters by */
  firmId?: string;
}

/**
 * MatterSelectorCombobox
 * ----------------------
 * A reusable combobox for selecting a Matter.
 * Leverages Catalyst's Combobox component and the `useGetMattersQuery` hook.
 *
 * Conventions:
 * - Selecting the sentinel "All matters" option will pass an empty string to `onChange`.
 * - Consumers can treat an empty string as "no filtering".
 */
export const MatterSelectorCombobox: React.FC<MatterSelectorComboboxProps> = ({
  value,
  onChange,
  className,
  placeholder = 'Select a matter',
  'aria-label': ariaLabel = 'Matter selector',
  includeAllOption = true,
  excludeFilter,
  isOptionDisabled,
  firmId,
}) => {
  // Track search input for server-side filtering
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Fetch matters with server-side search when query is provided
  const { data: mattersData, isLoading } = useGetMattersQuery(
    firmId
      ? { firm: [firmId], search: debouncedSearch || undefined }
      : debouncedSearch
        ? { search: debouncedSearch }
        : undefined,
  );
  const matters = useMemo(() => mattersData?.results ?? [], [mattersData]);

  // Build the list of options (optionally prefixing the sentinel "All matters" entry).
  const options = useMemo(() => {
    // Apply excludeFilter to hide options entirely
    const filteredMatters = excludeFilter
      ? matters.filter((m) => !excludeFilter(m))
      : matters;
    const typedMatters: (BaseMatter | { id: string; name: string })[] = filteredMatters;
    return includeAllOption
      ? [{ id: '', name: '-- All matters --' }, ...typedMatters]
      : typedMatters;
  }, [matters, excludeFilter, includeAllOption]);

  // Resolve the currently selected option object.
  const selectedOption = useMemo(() => {
    return (
      options.find((opt) => opt.id === value) ??
      (includeAllOption ? options[0] : undefined)
    );
  }, [options, value, includeAllOption]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div
        className={clsx(
          'h-10 w-full min-w-[200px] animate-pulse rounded-md bg-gray-200 dark:bg-gray-700',
          className,
        )}
      />
    );
  }

  return (
    <Combobox
      value={selectedOption}
      onChange={(opt) => onChange(opt?.id ?? '')}
      options={options}
      displayValue={(opt) =>
        opt ? (opt.name ?? `Matter ${String(opt.id).slice(0, 8)}`) : ''
      }
      filter={(opt, query) =>
        (opt.name ?? '').toLowerCase().includes(query.toLowerCase()) ||
        String(opt.id).toLowerCase().includes(query.toLowerCase())
      }
      onInputChange={setSearchInput}
      aria-label={ariaLabel}
      placeholder={placeholder}
      className={clsx('min-w-[200px]', className)}
      panelClassName="z-[60]"
      anchor="bottom"
    >
      {(option) => (
        <ComboboxOption
          key={option.id}
          value={option}
          disabled={
            Boolean(option.id) &&
            Boolean(isOptionDisabled?.(option as BaseMatter))
          }
        >
          <ComboboxLabel>
            {option.name ?? `Matter ${String(option.id).slice(0, 8)}`}
          </ComboboxLabel>
          {option.id && (
            <ComboboxDescription>
              {String(option.id).slice(0, 8)}
            </ComboboxDescription>
          )}
        </ComboboxOption>
      )}
    </Combobox>
  );
};

MatterSelectorCombobox.displayName = 'MatterSelectorCombobox';

export default MatterSelectorCombobox;
